import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import { TextField, InputAdornment, Select, MenuItem } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import FilterIcon from "../../../images/filter.png";
import CommonTableHead from "./CommonTableHead";
import ChamberLoading from "../ChamberLoading";
import i18n from "../../../locales/i18n";
import { CommonTableProps, Order, HeadCell } from "./types";
import { stableSort, getComparator, filterBySearch } from "./tableUtils";
import {
  useCommonTableStyles,
  bodyCellStyle,
  getRowBackgroundColor,
  paperStyles,
  tableContainerStyles,
  DEFAULT_ROW_HEIGHT,
} from "./CommonTableStyles";

export default function CommonTable<T extends Record<string, any>>({
  data,
  columns,
  defaultSortColumn,
  defaultSortOrder = "asc",
  searchPlaceholder = "Search...",
  searchPlaceholderColor = "#9E9E9E",
  searchFields,
  filters,
  hideSearch = false,
  onRowClick,
  rowHeight = DEFAULT_ROW_HEIGHT,
  rowKeyField = "id",
  renderCell,
  maxHeight = "80vh",
  footerContent,
  loading = false,
  emptyMessage,
  expandable = false,
  renderExpandedContent,
  expandedRowKeys: controlledExpandedKeys,
  onExpandChange,
}: CommonTableProps<T>) {
  const classes = useCommonTableStyles();

  // Sorting state
  const [order, setOrder] = React.useState<Order>(defaultSortOrder);
  const [orderBy, setOrderBy] = React.useState<string>(
    defaultSortColumn || (columns[0]?.id ?? "")
  );

  // Search state
  const [searchInput, setSearchInput] = React.useState("");

  // Expandable rows state (internal)
  const [internalExpandedKeys, setInternalExpandedKeys] = React.useState<string[]>([]);

  // Use controlled or internal expanded state
  const expandedKeys = controlledExpandedKeys !== undefined ? controlledExpandedKeys : internalExpandedKeys;

  // Handle expand toggle
  const handleExpandToggle = React.useCallback(
    (rowKey: string) => {
      const newExpandedKeys = expandedKeys.includes(rowKey)
        ? expandedKeys.filter((key) => key !== rowKey)
        : [...expandedKeys, rowKey];

      if (controlledExpandedKeys === undefined) {
        setInternalExpandedKeys(newExpandedKeys);
      }

      if (onExpandChange) {
        onExpandChange(newExpandedKeys);
      }
    },
    [expandedKeys, controlledExpandedKeys, onExpandChange]
  );

  // Handle sort request
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  };

  // Handle row click
  const handleRowClick = React.useCallback(
    (row: T) => {
      if (onRowClick) {
        onRowClick(row);
      }
    },
    [onRowClick]
  );

  // Filter and sort data
  const filteredData = React.useMemo(() => {
    return filterBySearch(data, searchInput, searchFields);
  }, [data, searchInput, searchFields]);

  const visibleRows = React.useMemo(() => {
    return stableSort(filteredData, getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);

  // Default cell renderer
  const defaultRenderCell = (column: HeadCell, row: T, rowIndex: number) => {
    const value = row[column.id];
    if (value === null || value === undefined) {
      return "-";
    }
    return String(value);
  };

  // Check if we should show search/filter bar
  const showToolbar = !hideSearch || (filters && filters.length > 0);

  return (
    <Box sx={{ width: "100%" }}>
      {showToolbar && (
        <div className={classes.searchContainer}>
          {!hideSearch && (
            <TextField
              variant="outlined"
              size="small"
              type="text"
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              value={searchInput}
              className={classes.searchBar}
              style={{ "--search-placeholder-color": searchPlaceholderColor } as React.CSSProperties}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {filters?.map((filter) => (
            <Select
              key={filter.id}
              className={classes.filterSelect}
              value={filter.value}
              onChange={(e) => filter.onChange(e.target.value as string)}
              variant="outlined"
              displayEmpty
              style={{ color: filter.value === "all" ? "#9E9E9E" : "#000" }}
              startAdornment={
                <InputAdornment position="start">
                  <img
                    src={FilterIcon}
                    alt="Filter"
                    style={{
                      width: "18px",
                      height: "18px",
                      marginRight: "8px",
                    }}
                  />
                </InputAdornment>
              }
            >
              <MenuItem value="all">{filter.placeholder}</MenuItem>
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ))}
        </div>
      )}

      <Paper sx={paperStyles}>
        <TableContainer sx={{ ...tableContainerStyles, maxHeight }}>
          <Table
            sx={{
              minWidth: "100%",
              width: "100%",
              tableLayout: "fixed",
            }}
            aria-labelledby="tableTitle"
            size="medium"
            stickyHeader
          >
            <CommonTableHead
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    style={{ padding: "40px" }}
                  >
                    <ChamberLoading size={60} />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    style={{ padding: "40px" }}
                  >
                    {emptyMessage ?? i18n.t("userFacingErrors.nothingToShow")}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, rowIndex) => {
                  const rowKey = String(row[rowKeyField] || rowIndex);
                  const backgroundColor = getRowBackgroundColor(rowIndex);
                  const isExpanded = expandable && expandedKeys.includes(rowKey);

                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow
                        hover
                        onClick={() => {
                          if (expandable) {
                            handleExpandToggle(rowKey);
                          } else {
                            handleRowClick(row);
                          }
                        }}
                        role="checkbox"
                        tabIndex={-1}
                        sx={{
                          cursor: expandable || onRowClick ? "pointer" : "default",
                          backgroundColor,
                          height: `${rowHeight}px`,
                        }}
                      >
                        {columns.map((column) => {
                          // Handle expand icon column
                          if (column.id === "expandIcon" && expandable) {
                            return (
                              <TableCell
                                key={column.id}
                                align="center"
                                style={{
                                  ...bodyCellStyle,
                                  backgroundColor,
                                  width: column.width || "50px",
                                  padding: "8px",
                                }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon style={{ color: "#000" }} />
                                ) : (
                                  <KeyboardArrowDownIcon style={{ color: "#000" }} />
                                )}
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell
                              key={column.id}
                              align={column.alignRight ? "right" : column.alignCenter ? "center" : "left"}
                              style={{
                                ...bodyCellStyle,
                                backgroundColor,
                                width: column.width,
                              }}
                            >
                              {renderCell
                                ? renderCell(column, row, rowIndex)
                                : defaultRenderCell(column, row, rowIndex)}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Expanded content row */}
                      {expandable && renderExpandedContent && (
                        <TableRow>
                          <TableCell
                            style={{
                              paddingBottom: 0,
                              paddingTop: 0,
                              borderBottom: isExpanded ? "1px solid #E8E8E8" : "none",
                            }}
                            colSpan={columns.length}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              {renderExpandedContent(row, rowIndex)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {footerContent}
      </Paper>
    </Box>
  );
}

// Re-export types for convenience
export * from "./types";
export * from "./tableUtils";
export * from "./CommonTableStyles";
